import { useState } from 'react';

import Link from 'next/link';

import { User } from '@supabase/supabase-js';

import { ChevronDown, XIcon } from 'lucide-react';
import { IoMenuSharp } from 'react-icons/io5';

import { buttonVariants } from '@kit/ui/button';
import { cn } from '@kit/ui/utils';

import LogoGradient from '../branding/logo-gradient';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../collapsible';
import { IconButton } from '../ui';
import { siteConfig } from './marketing-config';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from './ui/drawer';

export default function DrawerDemo({ user }: { user: User | null }) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const closeDrawer = () => setIsDrawerOpen(false);

  return (
    <Drawer
      open={isDrawerOpen}
      onOpenChange={setIsDrawerOpen}
      onClose={closeDrawer}
      direction="right"
    >
      <DrawerTrigger aria-label="Open menu">
        <IoMenuSharp color="white" className="text-2xl" />
      </DrawerTrigger>
      <DrawerContent
        className="h-[100dvh] w-screen overflow-y-auto overflow-x-hidden border-none bg-landing-background bg-cover bg-center bg-no-repeat text-white"
        style={{ backgroundImage: 'url(/images/drawer-background.jpg)' }}
      >
        <DrawerHeader className="flex h-full flex-col overflow-y-auto px-6 py-5">
          <div className="flex w-full items-center justify-between">
            <Link
              href="/"
              title="brand-logo"
              className="relative space-x-2"
              onClick={closeDrawer}
            >
              <LogoGradient className="h-[22px] w-auto" />
            </Link>
            <IconButton
              className="h-6 w-6 p-0"
              variant="ghost"
              Icon={() => <XIcon size={24} className="text-white" />}
              onClick={closeDrawer}
            />
          </div>
          <nav className="flex-1 overflow-y-auto">
            <ul className="mt-10 flex flex-col gap-8 text-left">
              {siteConfig.header.map((item, index) => (
                <li key={index}>
                  {'trigger' in item ? (
                    <Collapsible>
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center gap-4">
                          <span className="text-[32px] font-semibold leading-[1.2]">
                            {item.trigger}
                          </span>
                          <ChevronDown className="text-[#E6E6E699]" size={20} />
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent
                        className={cn(
                          'text-popover-foreground outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2', // animation
                          'space-y-2 pl-3',
                        )}
                      >
                        <ul className="mt-4 flex flex-col gap-4 border-l-2 border-white/40 pl-4">
                          {item.content.items.map((subItem, subIndex) => (
                            <li key={subIndex}>
                              <Link
                                onClick={closeDrawer}
                                href={subItem.href || ''}
                                className="text-xl font-medium text-white/70"
                              >
                                {subItem.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </CollapsibleContent>
                    </Collapsible>
                  ) : (
                    <Link
                      onClick={closeDrawer}
                      href={item.href || ''}
                      className="text-[32px] font-semibold leading-[1.2]"
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </DrawerHeader>
        <DrawerFooter className="px-6 pb-10">
          <div className="flex gap-4">
            {!user && (
              <Link
                onClick={closeDrawer}
                href="/auth/sign-in"
                className={cn(
                  buttonVariants({ variant: 'outline' }),
                  'h-10 w-full rounded-full bg-white/10 px-4 py-2 font-medium leading-5 text-white',
                )}
                style={{
                  boxShadow:
                    '0px -4px 14px 0px rgba(255, 255, 255, 0.10) inset, 0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                }}
              >
                Login
              </Link>
            )}
            <Link
              onClick={closeDrawer}
              href="/signup"
              className={cn(
                buttonVariants({ variant: 'default' }),
                'h-10 w-full rounded-full bg-white px-4 py-2 font-medium leading-5 text-black',
              )}
              style={{
                boxShadow:
                  '0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px -1px rgba(0, 0, 0, 0.10)',
              }}
            >
              Contact Us
            </Link>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
